import React from 'react';
import { Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';

function Contact() {
  const [name, setName] = React.useState('');
  const [email, setEmail] = React.useState('');
  const [company, setCompany] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [errors, setErrors] = React.useState<Record<string, string>>({});

  function validate(): boolean {
    const next: Record<string, string> = {};
    const workEmail =
      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) &&
      !/@(gmail|yahoo|icloud|hotmail|outlook|aol|protonmail)\./i.test(email);
    if (!name.trim())    next.name    = 'Required';
    if (!workEmail)      next.email   = 'Use a valid work email';
    if (!message.trim()) next.message = 'Required';
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const res = await fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, email, company, message }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        setErrors(j.errors || { form: 'Submission failed. Please try again.' });
        return;
      }
      setSubmitted(true);
      setName(''); setEmail(''); setCompany(''); setMessage('');
    } catch {
      setErrors({ form: 'Network error. Please try again.' });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Container className="py-5 text-light">
      <Row className="justify-content-center mb-4">
        <Col lg={8}>
          <h1 className="h2 fw-bold">Contact Arkodomo</h1>
          <p className="text-white-50 mb-0">
            We'd love to hear from you. For waitlist inquiries and partnerships, reach out below.
          </p>
        </Col>
      </Row>
      <Row className="justify-content-center">
        <Col lg={8}>
          <div className="p-3 bg-dark rounded-3">
            <p className="mb-3">
              Email: <a href="mailto:hello@arkodomo.com">hello@arkodomo.com</a>
            </p>
            {submitted && (
              <div className="alert alert-success" role="alert">
                Thanks — we'll follow up shortly.
              </div>
            )}
            {errors.form && (
              <div className="text-danger mb-2" role="alert">{errors.form}</div>
            )}
            <Form onSubmit={onSubmit} noValidate>
              <Row className="g-3">
                <Col md={6}>
                  <Form.Group controlId="name">
                    <Form.Label>Name</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Your name"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      isInvalid={!!errors.name}
                    />
                    <Form.Control.Feedback type="invalid">{errors.name}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Group controlId="email">
                    <Form.Label>Work Email</Form.Label>
                    <Form.Control
                      type="email"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      isInvalid={!!errors.email}
                    />
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="company">
                    <Form.Label>Company (optional)</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Company"
                      value={company}
                      onChange={(e) => setCompany(e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={12}>
                  <Form.Group controlId="message">
                    <Form.Label>Message</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      placeholder="How can we help?"
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      isInvalid={!!errors.message}
                    />
                    <Form.Control.Feedback type="invalid">{errors.message}</Form.Control.Feedback>
                  </Form.Group>
                </Col>

                <Col md={12}>
                  <Button
                    type="submit"
                    variant="primary"
                    disabled={submitting}
                    aria-label="Send message"
                  >
                    {submitting ? (
                      <><Spinner size="sm" className="me-2" />Sending</>
                    ) : (
                      'Send'
                    )}
                  </Button>
                </Col>
              </Row>
            </Form>
          </div>
        </Col>
      </Row>
    </Container>
  );
}

export default Contact;
